import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PhaseProvider } from '../../providers/phase-provider.services';
import { Subscription } from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialogActions, MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatOption, MatOptionSelectionChange } from '@angular/material/core';
import { Phase } from "../../../common/models/phase";
import { AnnualPlanPhase } from "../../../common/models/annual-plan.phase";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { Organization } from "../../../common/models/organization";
import { LessThanNumberAnnualPlanValidator } from "../../../common/validators/less-than-number-annual-plan.validator";
import {
  GreaterThanNumberAnnualPlanValidator
} from "../../../common/validators/greater-than-number-annual-plan.validator";
import { TranslateModule } from "@ngx-translate/core";
import { MatError, MatFormField } from "@angular/material/form-field";
import { MatSelect } from "@angular/material/select";
import { MatButton } from "@angular/material/button";
import { NgForOf, NgIf } from "@angular/common";
import { FlexLayoutModule } from "@angular/flex-layout";

@Component({
  selector: 'annual-plan-dialog-phase',
  templateUrl: './annual-plan-dialog-form-phase.component.html',
  styleUrls: ['./annual-plan-dialog-form-phase.scss'],
  imports: [
    TranslateModule,
    MatDialogTitle,
    MatDialogContent,
    MatError,
    MatFormField,
    MatSelect,
    MatOption,
    ReactiveFormsModule,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    NgIf,
    NgForOf,
    FlexLayoutModule
  ],
  standalone: true
})
export class AnnualPlanDialogFormPhaseComponent implements OnInit, OnDestroy {

    public phaseForm: FormGroup;
    public isSubmitting = false;
    public phases: Phase[] = [];

    public phase: Phase = {} as Phase;
    public annualPlanPhase: AnnualPlanPhase = {} as AnnualPlanPhase;
    public weeksMenuFrom: WeekMenu[] = [];
    public weeksMenuTo: WeekMenu[] = [];

    private organization: Organization;
    private subscriptions: Subscription[] = [];

    constructor(
        public dialog: MatDialogRef<AnnualPlanDialogFormPhaseComponent>,
        private utils: UtilsProvider,
        private fb: FormBuilder,
        private phaseProvider: PhaseProvider,
        @Inject(MAT_DIALOG_DATA) public data: any) {

        const weekMenuFrom = {};
        if (data.week) {
            Object.assign(weekMenuFrom, {number: data.week.weekNumber, realNumber: data.week.realWeekNumber});
        }

        this.organization = data.organization;

        // use the FormBuilder to create a form group
        this.phaseForm = this.fb.group({
            id: new FormControl(null),
            weekFrom: new FormControl(weekMenuFrom, [Validators.required]),
            weekTo: new FormControl(null, [Validators.required]),
            phase: new FormControl(null, [Validators.required]),
            tag: new FormControl(null),
        }, {
            validator: [
                LessThanNumberAnnualPlanValidator('weekFrom', 'weekTo', true),
                GreaterThanNumberAnnualPlanValidator('weekTo', 'weekFrom', true),
            ]
        });

        this.disableWeeksMenuFrom(data);

        this.weeksMenuTo = data.weeks.map(function(week:any) {
            return {
                number: week.weekNumber,
                realNumber: week.realWeekNumber,
                realWeekYear: week.realWeekYear,
                isDisabled: false,
            };
        });
    }

    public ngOnInit() {
        if (this.data.annualPlanPhase) {
            this.phaseForm.patchValue({
                id: this.data.annualPlanPhase.id,
                weekFrom: {number: this.data.annualPlanPhase.weekFrom},
                weekTo: {number: this.data.annualPlanPhase.weekTo},
                phase: this.data.annualPlanPhase.phase,
                tag: this.data.annualPlanPhase.tag
            });
        }

        this.subscriptions.push(
            this.phaseProvider.findAll(this.organization).subscribe((phases) => {
                this.phases = phases;
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public compareWeekMenus(o1: WeekMenu, o2: WeekMenu): boolean {
        if (null === o1 || null === o2) {
            return false;
        }

        return o1.number === o2.number;
    }

    public comparePhases(o1: Phase, o2: Phase): boolean {
        if (null === o1 || null === o2) {
            return false;
        }

        return o1.name === o2.name && o1.id === o2.id;
    }

    public submitForm() {
        this.isSubmitting = true;

        if (this.phaseForm.valid) {
            Object.assign(this.annualPlanPhase, this.phaseForm.value, {
                weekFrom: this.phaseForm.controls['weekFrom'].value.number,
                weekTo: this.phaseForm.controls['weekTo'].value.number
            });

            this.dialog.close({annualPlanPhase: this.annualPlanPhase, toDelete: false});
        } else {
            this.utils.validateAllFormFields(this.phaseForm);
            this.isSubmitting = false;
        }
    }

    public onDelete() {
        this.dialog.close({annualPlanPhase: this.data.annualPlanPhase, toDelete: true});
    }

    get f() {
        return this.phaseForm.controls;
    }

    public updateWeeksMenuTo(event: MatOptionSelectionChange, selectedNumber:any) {

        if (event.source.selected) {

            const weeksMenuTo = JSON.parse(JSON.stringify(this.weeksMenuFrom));

            let disableNextItems = false;
            for (const item of weeksMenuTo) {
                if (item.number < selectedNumber) {
                    item.isDisabled = true;

                    continue;
                }

                if (item.number >= selectedNumber && !disableNextItems) {
                    if (item.isDisabled) {
                        disableNextItems = true;
                    }
                }

                if (disableNextItems) {
                    item.isDisabled = true;
                }
            }

            this.weeksMenuTo = weeksMenuTo;
        }
    }

    private disableWeeksMenuFrom(data:any) {

        const annualPlanPhases = data.annualPlanPhases;
        const openedPhase = this.data.annualPlanPhase;

        /**
         * Cette fonction supprime les semaines de début de la phase
         * qui ne sont pas sélectionnable. Une semaine peut être
         * sélectionnée lorsqu'elle n'est pas impactée par une phase.
         * Autrement dit, toutes les semaines faisant partie d'une
         * phase sont retirées de la liste.
         *
         * On a alors 2 cas de figure à traiter :
         *
         * 1) On est en train d'ajouter une nouvelle phase (openedPhase === undefined).
         *    Dans ce cas, je retire toutes les semaines impactées par une phase, de la
         *    liste des possibilités.
         *
         * 2) On est en train de modificer une phase existante (openedPhase !== undefined).
         *    Dans ce cas, je retire toutes les semaines impactées paur une phase, sauf
         *    les semaines faisant partie de la phase en édition, de la liste des possibilités.
         *    On garde les semaines de la phase en édition afin de pouvoir choisir une plage
         *    plus petite par exemple : le début fixé à la semaine 15 est déplacé à la semaine
         *    16.
         */
        this.weeksMenuFrom = data.weeks.map(function(week:any) {

            // par défaut on garde la semaine
            let isDisabled = false;

            if (annualPlanPhases) {
                for (const annualPlanPhase of annualPlanPhases) {

                    // cas 2 : édition d'une phase
                    if (openedPhase !== undefined && annualPlanPhase.tag === openedPhase.tag) {

                        // je garde cette semaine, elle fait partie de la phase en édition
                        // je ne change pas le flag
                    } else {
                        if (week.weekNumber >= annualPlanPhase.weekFrom && week.weekNumber <= annualPlanPhase.weekTo) {

                            // la semaine fait partie d'une phase existante
                            isDisabled = true;
                        }
                    }
                }
            }

            return {
                number: week.weekNumber,
                realNumber: week.realWeekNumber,
                realWeekYear: week.realWeekYear,
                isDisabled,
            };
        }.bind(this));
    }
}

export interface WeekMenu {
    number: number;
    realNumber: number;
    realWeekYear: number;
    isDisabled: boolean;
}
